import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Medal, Award } from 'lucide-react';
import api, { getAssetUrl } from '../../utils/api';

const LeaderboardWidget = () => {
  const [leaders, setLeaders] = useState([]);
  const [latestBadge, setLatestBadge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchLeaderboard(), fetchLatestBadge()]).finally(() => setLoading(false));
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await api.get('/leaderboard?limit=10');
      setLeaders(res.data.leaderboard || []);
    } catch (err) {
      console.error('Leaderboard error:', err);
    }
  };

  const fetchLatestBadge = async () => {
    try {
      const res = await api.get('/badges/latest');
      setLatestBadge(res.data.badge || null);
    } catch (err) {
      console.error('Latest badge error:', err);
    }
  };

  const getRankIcon = (i) => {
    if (i === 0) return <Trophy className="w-4 h-4 text-[#FFD700]" />;
    if (i === 1) return <Medal className="w-4 h-4 text-[#C0C0C0]" />;
    if (i === 2) return <Award className="w-4 h-4 text-[#CD7F32]" />;
    return <span className="font-mono text-xs text-gray-600 w-4 text-center">#{i + 1}</span>;
  };

  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded p-4 mb-4" data-testid="leaderboard-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm text-[#C5A52A] tracking-wider">GETTIN BADGED</h3>
        <Trophy className="w-4 h-4 text-[#C5A52A]" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#C5A52A]" />
        </div>
      ) : (
        <>
          {leaders.length === 0 ? (
            <p className="font-mono text-xs text-gray-600 text-center py-4">No leaders yet</p>
          ) : (
            <div className="space-y-1">
              {leaders.map((leader, i) => (
                <Link
                  key={leader.user_id}
                  to={`/profile/${leader.user_id}`}
                  className="flex items-center gap-2 p-2 rounded hover:bg-[#1a1a1a] transition-colors group"
                  data-testid={`leader-row-${i}`}
                >
                  <div className="flex-shrink-0 w-6 flex justify-center">{getRankIcon(i)}</div>
                  {leader.profile_image_url ? (
                    <img
                      src={getAssetUrl(leader.profile_image_url)}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#1a1a1a] flex-shrink-0" />
                  )}
                  <span className="font-mono text-xs text-white truncate flex-grow group-hover:text-[#C5A52A] transition-colors">
                    {leader.name || 'Anonymous'}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {/* Latest Released Badge */}
          {latestBadge && (
            <div className="mt-3 pt-3 border-t border-[#1a1a1a]" data-testid="latest-badge-box">
              <p className="font-mono text-[10px] text-gray-500 tracking-wider mb-2">LATEST BADGE</p>
              <Link to="/badges" className="flex items-center gap-3 p-2 rounded bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#C5A52A]/30 transition-colors">
                {latestBadge.icon_url ? (
                  <img src={latestBadge.icon_url} alt={latestBadge.name} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded bg-[#C5A52A]/20 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#C5A52A]" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display text-xs text-white truncate">{latestBadge.name}</div>
                  {latestBadge.description && (
                    <div className="font-mono text-[10px] text-gray-500 truncate">{latestBadge.description}</div>
                  )}
                </div>
              </Link>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
            <Link to="/leaderboard" className="block text-center font-mono text-xs text-gray-500 hover:text-[#C5A52A] transition-colors">
              View Full Leaderboard
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default LeaderboardWidget;
